// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel, InvoiceLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import MultilineEllipsis from "@mcmc/XDL/Components/Common/MultiLineEllipsis";
import { mergeWithCss, Roboto } from "@mcmc/XDL/Utilities";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel | OrderLineModel | InvoiceLineModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    includeBrandName?: boolean;
    maxLine?: number;
    extendedStyles?: ProductDescriptionStyles;
}

type Props = OwnProps;

export interface ProductDescriptionStyles {
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    productDetailLink: {
        color: "text",
        hoverStyle: {
            color: "text",
            textDecoration: "underline",
        },
        css: css`
            width: 100%;
        `,
        typographyProps: {
            ...Roboto,
            size: 16,
            lineHeight: "20px",
            weight: "normal",
            css: css`
                letter-spacing: 0.2px;
            `,
        },
    },
    productDetailDescription: {
        ...Roboto,
        size: 16,
        lineHeight: "20px",
        weight: "normal",
        css: css`
            letter-spacing: 0.2px;
            width: 100%;
        `,
    },
};

const ProductDescription = ({ product, onClick, extendedStyles, includeBrandName, maxLine = 2 }: Props) => {
    const [styles] = useState(() => mergeWithCss(productDescriptionStyles, extendedStyles));

    const description =
        "product" in product
            ? product.product.productTitle
            : product.shortDescription || ("description" in product ? product.description : "");
    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;
    const brandName = "product" in product ? product.product.brand?.name : product.brand?.name;

    const productLinkPrefix = `${brandName && includeBrandName ? `<strong>${brandName}</strong>` : ""}`;
    return productDetailPath && (!("isActiveProduct" in product) || product.isActiveProduct) ? (
        <Link
            {...styles.productDetailLink}
            href={productDetailPath}
            alt={`See product details for ${description}`}
            onClick={onClick}
            data-test-selector="productDescriptionLink"
            aria-label={description}
            data-product-number={`${"product" in product ? product.product?.productNumber : ""}`}
        >
            <MultilineEllipsis
                content={`${productLinkPrefix} ${description}`}
                maxLines={maxLine}
                as="h2"
                {...styles.productDetailLink?.typographyProps}
            />
        </Link>
    ) : (
        <MultilineEllipsis
            content={`${productLinkPrefix} ${description}`}
            maxLines={maxLine}
            {...styles.productDetailDescription}
            as="h2"
            aria-label={description}
        />
    );
};

export default ProductDescription;
